// epics/blockingSourceEpic.ts
import { EMPTY, from } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { isAnyOf } from '@reduxjs/toolkit'
import type { Epic } from '../../index.js'

import { Actions } from '../../Slice/index.js'
import { visualizationSlice } from '../../Slice/Visualization/VisualizationSlice.js'
import { projectSlice } from '../../Slice/Project/ProjectSlice.js'
import { isSourceAction } from '../../Slice/Visualization/VisualizationSliceTypes.js'

import { runPipeline } from '../../../View/Component/SampleGrouping/RunPipeline.js'
import { selectVizSamples } from '../../Slice/Visualization/SelectVisualizationSamples.js'
import { hash } from './Hashing.js'
import { computeFieldsIndex } from '../../Slice/Visualization/ComputeFieldsIndex.js'
import { resolveMetadataFields } from '../../Slice/Visualization/ResolveMetadataFields.js'
import { isLoadSavedVizAction } from '../../../View/Component/ConfigurableVisualization/Persistence/SavedViz.js'
import { orderSlice } from '../../Slice/Order/OrderSlice.js'
import { selectScopeLoaded } from '../../Slice/Visualization/ScopeLoaded.js'

export const blockingSourceEpic: Epic = (action$, state$) => {
  const visualizationActions = Actions[visualizationSlice.name]
  const projectActions = Actions[projectSlice.name]
  const orderActions = Actions[orderSlice.name]

  const upstream$ = action$.pipe(
    filter(
      isAnyOf(
        visualizationActions.createSource,
        visualizationActions.setSourceConfig,
        visualizationActions.setSourceState,
        visualizationActions.updateSourceShared,
        visualizationActions.loadSavedViz,
        projectActions.getProjectSamplesRequestComplete,
        projectActions.loadProjectOrdersRequestSuccess,
        visualizationActions.applySourceChanges,
        orderActions.getAllDatasetSuccess,
        orderActions.getOrderRequestSuccess
      )
    )
  )

  return upstream$.pipe(
    withLatestFrom(state$),
    switchMap(([act, state]) => {
      const sourceIds = isSourceAction((act as { payload?: unknown }).payload)
        ? [(act as { payload: { sourceId: string } }).payload.sourceId]
        : Object.keys(state.visualization.sources)

      if (sourceIds.length === 0) return EMPTY

      return from(sourceIds).pipe(
        withLatestFrom(state$),

        // samples finalized (block mode) and their orders loaded, both for this scope: the store can
        // still hold the previous order's or project's while this one's are on their way
        filter(([, s]) => {
          const loaded = selectScopeLoaded(s, s.visualization.scope)
          return loaded.samples && loaded.orders
        }),
        // policy gate
        filter(([sourceId, s]) => (s.visualization.sources[sourceId]?.policy?.mode ?? 'block') === 'block'),

        // stable run key
        map(([sourceId, s]) => {
          const src = s.visualization.sources[sourceId]
          const scopeId = s.visualization?.scope?.id ?? 'noScope'
          const datasetVersion = s.project.totalSamples
          const runKey = hash(sourceId, scopeId, datasetVersion, src?.config, src?.state, src?.shared)
          return { sourceId, runKey, state: s }
        }),
        distinctUntilChanged((a1, a2) => a1.runKey === a2.runKey),

        // do the work
        switchMap(({ sourceId, runKey, state: s }) => {
          const src = s.visualization.sources[sourceId]
          if (!src) return EMPTY

          const samples = selectVizSamples(s)

          // Explicit sample-slice restriction: when a source declares sampleIds, bucket only those samples.
          // Applied BEFORE the pipeline; the field index stays over the full scope so metadata
          // descriptors don't shift with the subset. Inert for the normal workspace (sampleIds undefined).
          const sampleIds = src.config.sampleIds
          const scopedSamples = sampleIds && sampleIds.length > 0 ? samples.filter((x) => sampleIds.includes(x.id)) : samples

          // 1) Ensure we have a serializable fields index in snapshot
          const hasIndex = !!src.snapshot?.metadataIndex
          const maybeIndexAction = !hasIndex
            ? [
                visualizationSlice.actions.metadataFieldsIndexReady({
                  sourceId,
                  index: computeFieldsIndex(samples)
                })
              ]
            : []

          // 2) Resolve live descriptors from the (now guaranteed) index
          const fieldsIndex = src.snapshot?.metadataIndex ?? computeFieldsIndex(samples)
          const fields = resolveMetadataFields(fieldsIndex)

          // 3) Run the pipeline with those fields
          const allBuckets = runPipeline(scopedSamples, src.config.pipeline, fields)

          let selectedKeys = src.state.selectedBucketKeys ?? []
          const payload = act.payload
          if (isLoadSavedVizAction(payload)) {
            // TODO: depending on the mode, this would be the right place to load a default set of buckets if so desired by the loadVisMode
            const restoredSource = payload.saved.sources[sourceId]
            if (restoredSource) {
              // TODO: filter not present keys
              selectedKeys = restoredSource.selectedBucketKeys ?? selectedKeys
            }
          }

          return from([
            visualizationActions.sourceStarted({ sourceId, sourceKey: hash('SRC', runKey) }),
            ...maybeIndexAction,
            visualizationActions.bucketsReady({ sourceId, buckets: allBuckets, selectedKeys, meta: { nSamples: scopedSamples.length } })
          ])
        })
      )
    })
  )
}
